import { useState, useEffect, useCallback, useMemo } from "react";
import { collection, query, where, orderBy, limit, startAfter, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";

/**
 * usePagination
 * Paginação baseada em cursor do Firestore (performática e barata).
 * 
 * @param {string} collectionName - Nome da coleção
 * @param {string} idTenant - ID do tenant para filtro obrigatório
 * @param {Object} options - { pageSize, orderField, orderDirection, filters }
 * 
 * Exemplo:
 *   const { data, loading, hasMore, loadMore, refresh } = usePagination(
 *       "members", idTenant, { pageSize: 20, orderField: "fullName" }
 *   );
 */
export const usePagination = (collectionName, idTenant, options = {}) => {
    const {
        pageSize = 20,
        orderField = "createdAt",
        orderDirection = "desc",
        filters = [],
        skip = false
    } = options;

    // Proteção contra loops infinitos: Memoiza os filtros se o conteúdo for o mesmo
    const serializedFilters = JSON.stringify(filters);
    const memoFilters = useMemo(() => {
        return filters;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serializedFilters]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(!skip);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(null);

    const buildQuery = useCallback((cursor = null) => {
        const constraints = [
            where("idTenant", "==", idTenant),
            ...memoFilters.map(f => where(f.field, f.operator, f.value)),
            orderBy(orderField, orderDirection),
            limit(pageSize)
        ];

        if (cursor) {
            constraints.push(startAfter(cursor));
        }

        return query(collection(db, collectionName), ...constraints);
    }, [collectionName, idTenant, pageSize, orderField, orderDirection, memoFilters]);

    // Carga inicial
    const loadInitial = useCallback(async () => {
        if (!idTenant || skip) return;
        setLoading(true);
        setError(null);

        try {
            const q = buildQuery();
            const snapshot = await getDocs(q);

            const docs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setData(docs);
            setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
            setHasMore(snapshot.docs.length === pageSize);
        } catch (err) {
            console.error("Erro na paginação:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [idTenant, buildQuery, pageSize, skip]);

    // Carregar mais (próxima página)
    const loadMore = useCallback(async () => {
        if (!hasMore || loading || !lastDoc) return;
        setLoading(true);

        try {
            const q = buildQuery(lastDoc);
            const snapshot = await getDocs(q);

            const docs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setData(prev => [...prev, ...docs]);
            setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
            setHasMore(snapshot.docs.length === pageSize);
        } catch (err) {
            console.error("Erro ao carregar mais:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [hasMore, loading, lastDoc, buildQuery, pageSize]);

    // Refresh (recarregar do zero)
    const refresh = useCallback(() => {
        setData([]);
        setLastDoc(null);
        setHasMore(true);
        loadInitial();
    }, [loadInitial]);

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    return { data, loading, error, hasMore, loadMore, refresh };
};
